import { AsyncPipe, NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  ButtonLauncherReadSpeakerComponent,
  ENV,
  Environment,
  isDesktop,
  LearnizLogic,
  LearnizState,
  LottieComponent,
  ScormService,
  WordingPipe,
} from '@jump-tools-front/shared';
import {
  BackgroundPatternImages,
  BackgroundType,
  CustomImage,
  GenericProgressStatus,
  LearnizProgression,
  SSOLinkMode,
} from '@jump-tools-front/shared-interfaces';
import { PrivacyComponent } from '@jumpbiz/jump-rgpd';
import { Store } from '@ngxs/store';
import { fromEvent, merge, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { SupportLearnizService } from '../../shared/services/support-learniz/support-learniz.service';
import { LandingContentComponent } from './landing-details/landing-contents/landing-content.component';
import { LandingStatsComponent } from './landing-details/landing-stats/landing-stats.component';
import { LandingSummaryComponent } from './landing-details/landing-summary/landing-summary.component';
import { LandingIntroComponent } from './landing-intro/landing-intro.component';

export enum DetailsItems {
  INIT = 'init',
  CONTENT = 'contents',
  SUMMARY = 'summary',
  RESULTS = 'stats',
}

@Component({
  selector: 'learniz-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  imports: [
    ButtonLauncherReadSpeakerComponent,
    RouterLink,
    LandingIntroComponent,
    NgClass,
    LottieComponent,
    LandingContentComponent,
    LandingSummaryComponent,
    LandingStatsComponent,
    MatButtonModule,
    MatProgressSpinnerModule,
    PrivacyComponent,
    AsyncPipe,
    WordingPipe,
  ],
})
export class LandingPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private _store = inject(Store);
  private _supportLearnizService = inject(SupportLearnizService);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _scormService = inject(ScormService);
  private _renderer = inject(Renderer2);
  private _cdr = inject(ChangeDetectorRef);
  private _environment = inject<Environment>(ENV);
  readonly model$ = this._store.select(LearnizState.getLearniz);
  readonly progress$ = this._store.select(LearnizState.getProgress);
  readonly locale$ = this._store.select(LearnizState.getLocale);
  readonly learnizPerimeter$ = this._store.select(LearnizState.getPerimeter);
  readonly learnizMode$ = this._store.select(LearnizState.mode);
  showMenu = false;
  modes = SSOLinkMode;

  hideLandingContent = false;
  hideLandingMenu = false;
  hideLandingMainScreen = false;
  hidePrivacy = false;
  hideStats = false;
  hideSupport = false;
  isDesktopLayout = isDesktop;

  learnizStatus = GenericProgressStatus;
  isLoading = false;

  lottieClass: string;
  imagesTypes = BackgroundType;
  menuImage: CustomImage;

  detailsItems = DetailsItems;

  splitClass = 'split-content';
  splitContentLeft: HTMLElement = document.getElementById('landing-content-left');
  splitContentRight: HTMLElement = document.getElementById('landing-content-right');
  splitSummaryLeft: HTMLElement = document.getElementById('landing-summary-left');
  splitSummaryRight: HTMLElement = document.getElementById('landing-summary-right');
  splitStatsLeft: HTMLElement = document.getElementById('landing-stats-left');
  splitStatsRight: HTMLElement = document.getElementById('landing-stats-right');

  env = environment;
  private readonly _destroy$ = new Subject<void>();

  ngOnInit(): void {
    this._renderer.addClass(document.body, 'is-landing-page');
    const learniz = this._store.selectSnapshot(LearnizState.getLearniz);

    this.hideStats = learniz.params.hideStats || environment.isLocal;
    this.hideLandingContent = learniz.params.hideLandingContent;
    this.hideLandingMenu = learniz.params.hideLandingMenu;
    this.hideLandingMainScreen = learniz.params.hideLandingMainScreen;
    this.hidePrivacy = learniz.params.hidePrivacy || this._scormService.isScormActive || this._environment.isLocal;
    this.hideSupport = learniz.params.hideHelp || this._scormService.isScormActive || this._environment.isLocal;

    if (this.hideLandingMenu && this.hideLandingMainScreen) {
      this._router.navigate(['../chapter/0/0'], { relativeTo: this._route });
    }

    const styleParams = learniz.params.styleParams;
    this.menuImage = styleParams.landingBackgroundImages
      ? styleParams.landingBackgroundImages.menu
      : <CustomImage>environment.defaultStyleProperties.landingBackgroundImages.menu;
    const ua = window.navigator.userAgent;
    const msie = ua.indexOf('MSIE');

    if (this.isDesktopLayout && msie === -1 && !navigator.userAgent.match(/Trident.*rv:11\./)) {
      let isScrolling = false;
      merge(
        fromEvent(window, 'scroll').pipe(map(() => true)), // scrolling
        fromEvent(window, 'scroll').pipe(
          debounceTime(100),
          map(() => false),
        ), // stop scrolling
      )
        .pipe(takeUntil(this._destroy$), distinctUntilChanged())
        .subscribe(v => (isScrolling = v));

      fromEvent(document.body, 'wheel')
        .pipe(
          takeUntil(this._destroy$),
          filter(() => !isScrolling),
          map(e => e as WheelEvent),
          tap(e => (e.deltaY > 0 ? this.scrollToSplitScreen() : this.scrollToIntro())),
        )
        .subscribe();
    }
  }

  ngAfterViewInit(): void {
    this.splitContentLeft = document.getElementById('landing-content-left');
    this.splitContentRight = document.getElementById('landing-content-right');
    this.splitSummaryLeft = document.getElementById('landing-summary-left');
    this.splitSummaryRight = document.getElementById('landing-summary-right');
    this.splitStatsLeft = document.getElementById('landing-stats-left');
    this.splitStatsRight = document.getElementById('landing-stats-right');
    if (!this.hideLandingMenu) {
      this.split(DetailsItems.INIT);
    } else {
      document.getElementsByClassName('intro-bottom')[0].parentElement.lastElementChild.remove();
    }
    this._route.fragment.pipe(takeUntil(this._destroy$)).subscribe(fragment => this.split(fragment));
  }

  ngOnDestroy(): void {
    this._renderer.removeClass(document.body, 'is-landing-page');
    this._destroy$.next();
    this._destroy$.complete();
  }

  openSupportModal(): void {
    this._supportLearnizService.openModal$();
  }

  hasStarted(progress: LearnizProgression): boolean {
    return (
      progress &&
      progress.chapters.some(chapter => {
        const chapterStarted = LearnizLogic.isElementStarted(chapter);
        const someSlideStarted = chapter.slides.some(LearnizLogic.isElementStarted);
        return chapterStarted || someSlideStarted;
      })
    );
  }

  scrollToSplitScreen(id: string = 'details-split'): void {
    this.showMenu = false;
    document.querySelector(`#${id}`).scrollIntoView({ behavior: 'smooth' });
  }

  toggleMenu(): void {
    this.showMenu = !this.showMenu;
  }

  scrollToIntro(): void {
    document.querySelector('#container-intro').scrollIntoView({ behavior: 'smooth' });
  }

  split(detailsItem): void {
    detailsItem = detailsItem || DetailsItems.INIT;
    switch (detailsItem) {
      case DetailsItems.INIT:
      case DetailsItems.CONTENT:
        this.splitContent();
        break;
      case DetailsItems.SUMMARY:
        this.splitSummary();
        break;
      case DetailsItems.RESULTS:
        this.splitStats();
        break;
    }
    if (detailsItem !== DetailsItems.INIT) {
      this.scrollToSplitScreen(detailsItem);
      this._router.navigate(['.'], { fragment: detailsItem, relativeTo: this._route });
    } else {
      this.scrollToIntro();
      this._router.navigate(['.'], { fragment: '', relativeTo: this._route });
    }
    this._cdr.detectChanges();
  }

  splitContent(): void {
    this.splitClass = 'split-content';
    if (!this.hideLandingContent) {
      this.splitContentLeft.setAttribute('style', `transform: translateY(0%)`);
      this.splitContentRight.setAttribute('style', `transform: translateY(0%)`);
    }
    this.splitSummaryLeft.setAttribute('style', `transform: translateY(${!this.hideLandingContent ? '-100%' : '0%'})`);
    this.splitSummaryRight.setAttribute('style', `transform: translateY(${!this.hideLandingContent ? '100%' : '0%'})`);
    if (!this.hideStats) {
      this.splitStatsLeft.setAttribute('style', `transform: translateY(-200%)`);
      this.splitStatsRight.setAttribute('style', `transform: translateY(200%)`);
    }
  }

  splitSummary(): void {
    this.splitClass = 'split-summary';
    if (!this.hideLandingContent) {
      this.splitContentLeft.setAttribute('style', `transform: translateY(100%)`);
      this.splitContentRight.setAttribute('style', `transform: translateY(-100%)`);
    }
    this.splitSummaryLeft.setAttribute('style', `transform: translateY(0%)`);
    this.splitSummaryRight.setAttribute('style', `transform: translateY(0%)`);
    if (!this.hideStats) {
      this.splitStatsLeft.setAttribute('style', `transform: translateY(-100%)`);
      this.splitStatsRight.setAttribute('style', `transform: translateY(100%)`);
    }
  }

  splitStats(): void {
    this.splitClass = 'split-stats';
    if (!this.hideLandingContent) {
      this.splitContentLeft.setAttribute('style', `transform: translateY(${this.hideStats ? '100%' : '200%'})`);
      this.splitContentRight.setAttribute('style', `transform: translateY(${this.hideStats ? '-100%' : '-200%'})`);
    }
    this.splitSummaryLeft.setAttribute('style', `transform: translateY(${this.hideStats ? '0%' : '100%'})`);
    this.splitSummaryRight.setAttribute('style', `transform: translateY(${this.hideStats ? '0%' : '-100%'})`);

    if (!this.hideStats) {
      this.splitStatsLeft.setAttribute('style', `transform: translateY(0%)`);
      this.splitStatsRight.setAttribute('style', `transform: translateY(0%)`);
    }
  }

  isVertex(src: string): boolean {
    if (BackgroundPatternImages[0].alt !== 'vertex') {
      console.error('BackgroundPatternImages has changed, it should have the vertex lottie as first item');
    }
    return src === BackgroundPatternImages[0].src; // the first element must be the vertex
  }
}
